import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { ShowHomesResult } from '@propertypal/shared/src/types/show-homes';
import platform from '@propertypal/shared/src/utils/platform';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import PropertyGrid from '@propertypal/web-ui/src/property/PropertyGrid';
import { Heading, SubHeading } from '@propertypal/web-ui/src/typography';
import Head from 'next/head';
import React, { FunctionComponent, useMemo } from 'react';
import generateMetaTags from '../../components/layout/MetaTags';
import ShowHomesFilterBar from '../../components/show-homes/ShowHomesFilterBar';
import ShowHomesTable from '../../components/show-homes/ShowHomesTable';
import IMAGE_LOCATOR from '../../constants/imageLocator';
import { requireAuthToken } from '../../utils/auth';

interface Props {
  showHomes: ShowHomesResult;
  town: string;
}

const metaData = {
  title: `Show Home Opening Times - ${platform.title}`,
  description:
    'Want To See A Glorious Home In Person? View The Upcoming Show Home Opening Times Across Ireland &amp; Northern Ireland.',
  url: `/show-home-opening-times`,
};

export const getServerSideProps = requireAuthToken(async (ctx) => {
  const town = ctx?.query?.town ? ctx.query.town[0] : '';

  const result = await axios(
    {
      url: `${API_URL}/show-homes?imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}${
        town ? `&url=%2Fnew-developments%2F${town}` : ''
      }`,
      method: 'GET',
    },
    undefined,
    ctx,
  );

  if (town) {
    ctx.res.statusCode = result.data.showHomes.length > 0 ? 200 : 404;
  }

  return {
    props: {
      showHomes: result.data,
      town,
    },
  };
});

const ShowHomeOpeningTimes: FunctionComponent<Props> = (props) => {
  const townName = useMemo(
    () => props.town && props.showHomes.primaryTowns?.find((t) => t.urlComponent === props.town)?.uniqueName,
    [props.town, props.showHomes.primaryTowns],
  );

  return (
    <>
      <Head>{generateMetaTags(metaData)}</Head>

      <PageWrapper>
        <Heading mt={40} mb={40}>
          Show Home Opening Times
        </Heading>
      </PageWrapper>

      <ShowHomesFilterBar primaryTowns={props.showHomes.primaryTowns} town={props.town} />

      {!!props.showHomes.featured.length && (
        <PageWrapper>
          <SubHeading mt={32} mb={16}>
            Featured Show Homes{townName && ` in ${townName}`}
          </SubHeading>
        </PageWrapper>
      )}

      <PropertyGrid
        containerStyle={{ maxWidth: MAX_WIDTH - 20, margin: '0 auto' }}
        maxColumns={3}
        properties={props.showHomes.featured}
      />

      <ShowHomesTable
        showHomes={props.showHomes.showHomes}
        totalDevelopments={props.showHomes.totalDevelopments}
        townName={townName}
      />
    </>
  );
};

export default ShowHomeOpeningTimes;
